import { createI18n } from "vue-i18n";

export const DEFAULT_LOCALE = "ua";
export const LANG_STORAGE_KEY = "lang";

// Every src/locales/<code>.json file is picked up automatically,
// so adding a language only requires dropping a new file there.
const context = require.context("@/locales", false, /\.json$/);

const messages = {};
context.keys().forEach((key) => {
  const code = key.replace(/^\.\//, "").replace(/\.json$/, "");
  messages[code] = context(key);
});

export const availableLocales: { code: string; name: string }[] = Object.keys(
  messages
).map((code) => ({ code, name: messages[code].languageName || code }));

const getInitialLocale = (): string => {
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved && messages[saved]) return saved;
  } catch (e) {
    // storage unavailable
  }
  return DEFAULT_LOCALE;
};

// Slavic plural rule: 1 день | 2-4 дні | 5+ днів (falls back to default for 2-form locales)
const slavicPlural = (choice: number, choicesLength: number): number => {
  if (choicesLength < 3) {
    return choice === 1 ? 0 : 1;
  }
  const mod10 = choice % 10;
  const mod100 = choice % 100;
  if (mod10 === 1 && mod100 !== 11) return 0;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 1;
  return 2;
};

export const toLangTag = (code: string): string => (code === "ua" ? "uk" : code);

const i18n = createI18n({
  legacy: false,
  locale: getInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  globalInjection: true,
  pluralRules: { ua: slavicPlural }, // the Composer API calls this option "pluralRules"
  // some messages hold links and are printed as HTML; they all come from our own files in src/locales,
  // never from users or the server, so the "HTML in a message" warning is switched off
  warnHtmlMessage: false,
  messages,
});

document.documentElement.lang = toLangTag(i18n.global.locale.value);

export const setLocale = (code: string): void => {
  if (!messages[code]) return;
  i18n.global.locale.value = code;
  document.documentElement.lang = toLangTag(code);
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch (e) {
    // storage unavailable
  }
};

export default i18n;
